import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { DownloadIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { AButton } from "@/components/ui-kit/a-button.tsx";

import { apiGetMedicalReportsOptions, UploadStatus } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { AddButton } from "../../../components/ui-kit/add-button";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { PatientSubpage } from "../common/patient-subpage";
import { AddReportDialog } from "./add-report-dialog";
import { reportTypes, uploadStatuses } from "./enums";
import { MedicalReports } from "./reports";

export const ReportsRoute = observer(function ReportsRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const [model] = useState(
    () => new MedicalReports([], api.permissions, api.requireUserUUID()),
  );
  const [showAddDialog, setShowAddDialog] = useState(false);

  const { data, refetch } = useQuery(
    apiGetMedicalReportsOptions({
      query: { patient_ids: [patient.patientId] },
    }),
  );

  useEffect(() => {
    if (data) model.updateData(data.medical_reports);
  }, [data, model]);

  return (
    <PatientSubpage title={t`Reports`} loading={!model.initialized}>
      <DataTable
        filterActions={
          <AddButton onClick={() => setShowAddDialog(true)}>
            <Trans>Add report</Trans>
          </AddButton>
        }
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "report", other: "reports" })}`
        }
        columns={[
          {
            header: <Trans>Date</Trans>,
            attr: "report_date",
            type: "date",
            editable: true,
            sort: true,
            initSort: "desc",
            width: 100,
          },
          {
            header: <Trans>Type</Trans>,
            attr: "report_type",
            type: "enum",
            editable: true,
            values: reportTypes,
            filter: {
              kind: "multi",
              group: t`Report`,
              options: new Map(Object.entries(reportTypes)),
            },
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            multiline: true,
            editable: true,
            width: 200,
          },
          {
            header: <Trans>Status</Trans>,
            width: 100,
            cell: ({ item }) => (
              <span
                style={{
                  color:
                    item.upload_status === UploadStatus.success
                      ? "green"
                      : item.upload_status === UploadStatus.pending
                        ? undefined
                        : "red",
                }}
              >
                {resolveEnumLabel(uploadStatuses[item.upload_status])}
              </span>
            ),
          },
          {
            header: <Trans>Filename</Trans>,
            attr: "original_name",
            type: "string",
            multiline: 3,
          },
          {
            header: <Trans>Download</Trans>,
            width: 50,
            cell: ({ item }) =>
              item.upload_status === UploadStatus.success ? (
                <AButton
                  href={`${api.versionedRoute()}/medical-reports/download/${item.uuid}`}
                  target="_blank"
                  rel="noreferrer"
                  variant="outline"
                  size="sm"
                  aria-label={t`Download`}
                >
                  <DownloadIcon className="text-foreground" size={14} />
                </AButton>
              ) : null,
          },
        ]}
        onEdit={async (editedField, item) => {
          await patient.updateItem("medical-reports", item.uuid, editedField);
          await refetch();
        }}
        onDelete={async (item) => {
          await patient.deleteItem("medical-reports", item.uuid);
          await refetch();
        }}
      />

      {showAddDialog && (
        <AddReportDialog
          app={props.app}
          onClose={() => setShowAddDialog(false)}
          onSaved={refetch}
        />
      )}
    </PatientSubpage>
  );
});
